import { Component, OnInit, Inject, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';

import { PropertyService } from '../../services/property.service';
import { OrganizationService } from '../../services/organization.service';
import { NavigationContextService } from '../../services/navigation-context.service';

@Component({
  selector: 'app-start-lease',
  templateUrl: './start-lease.component.html',
  styleUrls: ['./start-lease.component.scss']
})
export class StartLeaseComponent implements OnInit {
  leaseForm: FormGroup;
  property: any;
  users: any[] = [];
  possibleDates: number[] = [];
  saving = false;
  importing = false;
  importFileName = '';
  importNotes = '';
  importError = '';
  importedDocumentIds: string[] = [];
  importedDocumentName = '';

  constructor(
    private fb: FormBuilder,
    private propertyService: PropertyService,
    private organizationService: OrganizationService,
    private route: ActivatedRoute,
    private router: Router,
    private navContext: NavigationContextService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Initialize date options (1-31)
    for (let i = 1; i <= 31; i++) {
      this.possibleDates.push(i);
    }

    // Initialize form with default values
    this.leaseForm = this.fb.group({
      selectedUser: [null],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      emailAddress: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', Validators.required],
      type: ['TERM', Validators.required],
      startDate: [null],
      endDate: [null],
      rollToMonthToMonth: [false],
      monthlyCost: [null, [Validators.required, Validators.min(0)]],
      deposit: [null, [Validators.required, Validators.min(0)]],
      invoiceDayOfMonth: [1, Validators.required],
      invoiceDueDayOfMonth: [5, Validators.required],
      lateFeePercent: [10, [Validators.required, Validators.min(0), Validators.max(100)]],
      lateFeeFrequency: [1, Validators.required],
      notify: [true],
      notes: ['']
    });

    // Add conditional validators for term leases
    const typeControl = this.leaseForm.get('type');
    if (typeControl) {
      typeControl.valueChanges.subscribe(type => {
        const startDateControl = this.leaseForm.get('startDate');
        const endDateControl = this.leaseForm.get('endDate');
        if (type === 'TERM') {
          if (startDateControl) {
            startDateControl.setValidators([Validators.required]);
          }
          if (endDateControl) {
            endDateControl.setValidators([Validators.required]);
          }
        } else {
          if (startDateControl) {
            startDateControl.clearValidators();
            startDateControl.setValue(null);
          }
          if (endDateControl) {
            endDateControl.clearValidators();
            endDateControl.setValue(null);
          }
        }
        if (startDateControl) {
          startDateControl.updateValueAndValidity();
        }
        if (endDateControl) {
          endDateControl.updateValueAndValidity();
        }
      });
    }
  }

  ngOnInit() {
    // Get property from route params or query params
    const propertyId = this.route.snapshot.paramMap.get('propertyId') || 
                      this.route.snapshot.queryParamMap.get('propertyId');
    
    if (propertyId) {
      this.propertyService.getPublicProperty(propertyId).then(p => {
        this.property = p;
      }).catch(err => {
        console.error('Error loading property:', err);
      });
    } else if (this.data && this.data.property) {
      this.property = this.data.property;
    } else {
      this.property = null;
    }

    // Load org (for user list + billing defaults that pre-fill the form)
    this.organizationService.getOrganization().then(org => {
      this.applyOrgBillingDefaults(org);
      this.organizationService.getUsers(org).then(users => {
        this.users = users || [];
      }).catch(err => {
        console.error('Error loading users:', err);
        this.users = [];
      });
    }).catch(err => {
      console.error('Error loading organization:', err);
    });
  }

  private applyOrgBillingDefaults(org: any) {
    if (!org) {
      return;
    }
    const patch: any = {};
    if (org.defaultInvoiceDayOfMonth != null) {
      patch.invoiceDayOfMonth = org.defaultInvoiceDayOfMonth;
    }
    if (org.defaultInvoiceDueDayOfMonth != null) {
      patch.invoiceDueDayOfMonth = org.defaultInvoiceDueDayOfMonth;
    }
    if (org.defaultLateFeePercent != null) {
      patch.lateFeePercent = org.defaultLateFeePercent;
    }
    if (org.defaultLateFeeFrequency != null) {
      patch.lateFeeFrequency = org.defaultLateFeeFrequency;
    }
    if (Object.keys(patch).length > 0) {
      this.leaseForm.patchValue(patch);
    }
  }

  onUserChange() {
    const selectedUserControl = this.leaseForm.get('selectedUser');
    const selectedUser = selectedUserControl ? selectedUserControl.value : null;
    if (selectedUser) {
      this.leaseForm.patchValue({
        firstName: selectedUser.firstName || '',
        lastName: selectedUser.lastName || '',
        emailAddress: selectedUser.emailAddress || '',
        phoneNumber: selectedUser.phoneNumber || ''
      });
    } else {
      this.leaseForm.patchValue({
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: ''
      });
    }
  }

  onImportLeaseDocument(event: any) {
    const input = event && event.target ? event.target : null;
    const file: File = input && input.files && input.files[0] ? input.files[0] : null;
    if (input) {
      input.value = '';
    }
    if (!file) {
      return;
    }
    if (!this.property || !this.property.id) {
      this.importError = 'Load a property before importing a lease document.';
      return;
    }

    this.importing = true;
    this.importError = '';
    this.importNotes = '';
    this.importFileName = file.name;

    this.propertyService.importLeaseDocument(this.property.id, file).then((res: any) => {
      this.importing = false;
      const draft = res && res.draft ? res.draft : null;
      if (!draft) {
        this.importError = 'No lease details were found in that document.';
        return;
      }
      if (res.documentId) {
        this.importedDocumentIds = [res.documentId];
        this.importedDocumentName = (res.document && res.document.originalFilename)
          ? res.document.originalFilename
          : file.name;
      }
      this.applyLeaseDraft(draft);
    }).catch((error) => {
      this.importing = false;
      console.error('Error importing lease document:', error);
      let msg = (error.error && error.error.error)
        ? error.error.error
        : (error.message || 'Failed to read lease document');
      // Keep the banner readable; full detail stays in the console.
      if (msg && msg.length > 220) {
        msg = msg.substring(0, 220).trim() + '…';
      }
      this.importError = msg;
      if (error.error && error.error.documentId) {
        this.importedDocumentIds = [error.error.documentId];
        this.importedDocumentName = file.name;
      }
    });
  }

  private applyLeaseDraft(draft: any) {
    const type = draft.type === 'M2M' ? 'M2M' : 'TERM';
    const patch: any = {
      selectedUser: null,
      firstName: draft.firstName || '',
      lastName: draft.lastName || '',
      emailAddress: draft.emailAddress || '',
      phoneNumber: draft.phoneNumber || '',
      type: type,
      rollToMonthToMonth: !!draft.rollToMonthToMonth,
      notify: draft.notify !== undefined ? !!draft.notify : true
    };

    if (draft.monthlyCost != null && draft.monthlyCost !== '') {
      patch.monthlyCost = draft.monthlyCost;
    }
    if (draft.deposit != null && draft.deposit !== '') {
      patch.deposit = draft.deposit;
    }
    if (draft.invoiceDayOfMonth != null) {
      patch.invoiceDayOfMonth = draft.invoiceDayOfMonth;
    }
    if (draft.invoiceDueDayOfMonth != null) {
      patch.invoiceDueDayOfMonth = draft.invoiceDueDayOfMonth;
    }
    if (draft.lateFeePercent != null) {
      patch.lateFeePercent = draft.lateFeePercent;
    }
    if (draft.lateFeeFrequency != null) {
      patch.lateFeeFrequency = draft.lateFeeFrequency;
    }
    if (draft.notes) {
      patch.notes = draft.notes;
    }

    // Patch type first so date validators update, then set dates.
    this.leaseForm.patchValue({ type: type });
    if (type === 'TERM') {
      patch.startDate = this.parseDraftDate(draft.startDate);
      patch.endDate = this.parseDraftDate(draft.endDate);
    } else {
      patch.startDate = null;
      patch.endDate = null;
      patch.rollToMonthToMonth = false;
    }

    this.leaseForm.patchValue(patch);

    // Match an existing org user by email when possible.
    const email = (draft.emailAddress || '').toLowerCase().trim();
    if (email && this.users && this.users.length) {
      for (let i = 0; i < this.users.length; i++) {
        const u = this.users[i];
        if (u && u.emailAddress && String(u.emailAddress).toLowerCase() === email) {
          this.leaseForm.patchValue({ selectedUser: u });
          break;
        }
      }
    }

    this.importNotes = draft.notes || '';
    if (draft.propertyAddress) {
      const note = 'Document property: ' + draft.propertyAddress;
      this.importNotes = this.importNotes ? (this.importNotes + ' · ' + note) : note;
    }
  }

  private parseDraftDate(value: any): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    const s = String(value).trim();
    // Prefer local-date construction for YYYY-MM-DD to avoid UTC day shift.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) {
      return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  onLeaseTypeChange() {
    // Already handled in form valueChanges subscription
  }

  getDaySuffix(day: number): string {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  onCancel() {
    const propertyId = (this.property && this.property.id) || this.navContext.propertyId;
    this.navContext.goToManageLeases(propertyId);
  }

  onSave() {
    if (!this.leaseForm.valid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.leaseForm.controls).forEach(key => {
        const control = this.leaseForm.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
      return;
    }

    this.saving = true;
    const formValue = this.leaseForm.value;
    const target = this.property || (this.data && this.data.property);

    // Prepare lease data
    const leaseData: any = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      emailAddress: formValue.emailAddress,
      phoneNumber: formValue.phoneNumber,
      type: formValue.type,
      monthlyCost: parseFloat(formValue.monthlyCost),
      deposit: parseFloat(formValue.deposit),
      invoiceDayOfMonth: parseInt(formValue.invoiceDayOfMonth, 10),
      invoiceDueDayOfMonth: parseInt(formValue.invoiceDueDayOfMonth, 10),
      lateFeePercent: parseFloat(formValue.lateFeePercent),
      lateFeeFrequency: parseInt(formValue.lateFeeFrequency, 10),
      notify: formValue.notify,
      rollToMonthToMonth: formValue.type === 'TERM' ? !!formValue.rollToMonthToMonth : false,
      notes: formValue.notes || null,
      documentIds: this.importedDocumentIds || []
    };

    // Add dates for term leases
    if (formValue.type === 'TERM' && formValue.startDate && formValue.endDate) {
      leaseData.startDate = this.formatDate(formValue.startDate);
      leaseData.endDate = this.formatDate(formValue.endDate);
    }

    // Add userId if existing user selected
    if (formValue.selectedUser) {
      leaseData.userId = formValue.selectedUser.id;
    }

    this.propertyService.startLease(target, leaseData).then(() => {
      this.saving = false;
      const propertyId = (target && target.id) || this.navContext.propertyId;
      this.navContext.goToManageLeases(propertyId);
    }).catch((error) => {
      this.saving = false;
      console.error('Error starting lease:', error);
      const errorMessage = (error.error && error.error.error) ? error.error.error : (error.message || 'Unknown error');
      alert('Failed to create lease: ' + errorMessage);
    });
  }

  formatDate(date: Date): string {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  close() {
    this.onCancel();
  }
}
